/* DraftWithAIButton — reusable "Draft with AI" pill for text fields.

   Calls POST /api/admin/ai/draft with a purpose ("about" | "gallery_welcome"
   | "share_note") and optional context, then writes the result into the
   target field via the provided onDraft(text) callback.

   Designed to be slipped next to a textarea label so the photographer can
   summon a starting draft without leaving the form. */

import { useState } from "react";
import { Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

export default function DraftWithAIButton({ purpose, context, onDraft, testid, className = "" }) {
  const [loading, setLoading] = useState(false);

  async function generate() {
    setLoading(true);
    try {
      const { data } = await api.post("/admin/ai/draft", { purpose, context });
      if (data?.text) {
        onDraft(data.text);
        toast.success("Drafted with Lumetier AI");
      } else {
        toast.error("No draft returned — try again");
      }
    } catch (err) {
      const status = err.response?.status;
      const detail = formatApiErrorDetail(err.response?.data?.detail);
      if (status === 429) toast.error(detail || "Daily AI limit reached");
      else if (status === 503) toast.error("AI service not configured for this studio");
      else toast.error(detail || "Couldn't draft right now");
    } finally {
      setLoading(false);
    }
  }

  return (
    <button
      type="button"
      onClick={generate}
      disabled={loading}
      className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--gold))] hover:text-[hsl(var(--gold))]/80 transition-colors disabled:opacity-60 ${className}`}
      data-testid={testid || `draft-ai-${purpose}`}
      title="Generate a starting draft with Lumetier AI"
    >
      {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
      {loading ? "Drafting" : "Draft with AI"}
    </button>
  );
}
